
import { ContainerShape, HexagonShape, InnerShape, InnerShape2 } from '../components/Shape'
import { motion } from 'framer-motion'
import { useSound } from '../components/SoundHandler'
import { useAccount } from 'wagmi'
import { useError } from '~/core/contexts/ErrorContext'
import { Address } from 'viem'
import { useResolution } from '../hooks/useResolution'
import { icons } from '../assets/icons/icons'
import { useState, useMemo } from 'react'
import { formatBalance } from '~/core/utils/formatBalance'
import { TOKENS } from '~/core/tokens'
import { ZERO_ADDRESS, GRAPHQL_URL } from '~/core/constants'
import Loader from '~/core/components/Loader'
import useSWR from 'swr'
import { api, BlinkoGame, BlinkoPaginatedGamesResponse, BlinkoPlayerStats, checkVersionMode } from '../api'
import { useModal } from '~/core/contexts/ModalContext'
import { useToast } from '~/core/contexts/ToastContext'
import { findAlreadyClaimedGames } from '../claimDiagnostics'

import { useBlinkoContract } from '../hooks/useBlinkoContract'
import { Hex } from 'viem'
import { ContinueModal } from './continue.modal'
import { RESOLVER_ADDRESS } from '../constants'

type SubgraphActiveGame = {
  id: string;
}

type QueryResult = {
  games: SubgraphActiveGame[];
}

const fetchUserActiveGames = async (address: Address): Promise<QueryResult> => {
  try {
    const responseSubgraph = await fetch(GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
            query {
            games (
                where: {
                player: "${address.toLowerCase()}",
                resolver: "${RESOLVER_ADDRESS.toLowerCase()}",
                status: Active
                }
            ) {
                id
            }
            }
        `
      }),
    });

    const { data } = await responseSubgraph.json();

    return {
      games: data.games || [],
    };
  } catch (e) {
    console.error(e);
    return { games: [] };
  }
};

const fetchUserStats = async (address: Address): Promise<BlinkoPlayerStats> => {
  try {
    const playerResolverTokenId = `${address.toLowerCase()}${RESOLVER_ADDRESS.slice(2).toLowerCase()}${ZERO_ADDRESS.slice(2).toLowerCase()}`
    const response = await fetch(GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
            query {
              playerResolverToken(id: "${playerResolverTokenId}") {
                id
                totalGames
                totalWins
                totalLosses
                totalBetAmount
                totalPayoutAmount
              }
            }
        `
      }),
    });

    const { data } = await response.json();
    const player = data?.playerResolverToken;

    return {
      totalGames: Number(player.totalGames || 0),
      totalWon: Number(player.totalWins || 0),
      totalLost: Number(player.totalLosses || 0),
      totalBet: player.totalBetAmount || '0',
      totalPayout: player.totalPayoutAmount || '0',
    };
  } catch (e) {
    console.error(e);
    return {
      totalGames: 0,
      totalWon: 0,
      totalLost: 0,
      totalBet: '0',
      totalPayout: '0',
    };
  }
}

const fetchUserGames = async (address: Address, page: number, itemsPerPage: number): Promise<BlinkoPaginatedGamesResponse> => {
  const offset = (page - 1) * itemsPerPage;
  return api.getPlayerGames(address, itemsPerPage, offset);
}

const useFetchStats = (address?: Address) => {
  return useSWR(address ? ['blinko-stats', address] : null, () => fetchUserStats(address!))
}

const useFetchHistory = (address: Address | undefined, page: number, items: number) => {
  return useSWR(
    address ? ['blinko-history', address, page, items] : null,
    () => fetchUserGames(address!, page, items)
  )
}

const useFetchActiveGames = (address?: Address) => {
    return useSWR(address ? ['blinko-active-games-graph', address] : null, () => fetchUserActiveGames(address!))
}

export const HistoryModal = ({ address, onDismiss }: { address?: Address, onDismiss: () => void }) => {
  const { address: _address } = useAccount()
  if (!address)
    address = _address!

  const { showModal } = useModal()
  const { handleError } = useError()
  const toast = useToast()
  const sound = useSound();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const { data: historyResponse, isLoading: isHistoryLoading, mutate: mutateHistory } = useFetchHistory(address, currentPage, itemsPerPage)
  const { data: statsData, mutate: mutateStats } = useFetchStats(address)
  const { data: activeGamesData, mutate: mutateActiveGames } = useFetchActiveGames(address)

  const { cashOutOnChain, settlingGames } = useBlinkoContract();

  const { isMobile } = useResolution()

  const activeGameIds = useMemo(() => {
      return new Set((activeGamesData?.games || []).map(g => g.id.toLowerCase()));
  }, [activeGamesData]);

  const onClose = () => {
    onDismiss()
    sound.play('click');
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    sound.play('click');
  }

  const handleActiveGameAction = async (game: BlinkoGame) => {
    sound.play('click');

    if (game.status === 'active') {
      showModal(hide => (
        <ContinueModal 
          game={game} 
          onDismiss={() => {
            hide();
            onClose(); 
          }} 
        />
      ), { hideOnEscapeKey: true });
      return;
    }

    if (game.status === 'won' && game.cashOutSignature) {
      try {
        await cashOutOnChain({
          gameId: game.gameId as Hex,
          payoutAmount: BigInt(game.payoutAmount || '0'),
          gameState: game.gameStateHash as Hex,
          gameSeed: game.gameSeed as Hex,
          deadline: BigInt(game.deadline),
          serverSignature: game.cashOutSignature as Hex,
        });

        activeGameIds.delete(game.gameId.toLowerCase());
        mutateHistory();
        mutateStats();
        mutateActiveGames();
      } catch (e) {
        console.error('Failed to claim game', e);
        const claimed = await findAlreadyClaimedGames([game], address)
          .catch(() => new Set<string>());
        if (claimed.has(game.gameId.toLowerCase())) {
          toast.error('Already cashed out — this game is settled. Refreshing…');
          mutateHistory();
          mutateStats();
          mutateActiveGames();
        } else {
          handleError('Failed to claim game', e);
        }
      }
    }
  }

  const historyData = historyResponse?.games || [];
  const totalItems = historyResponse?.pagination?.total || 0;

  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / itemsPerPage)
  }, [totalItems, itemsPerPage])

  return (
    <div className="fixed z-30 inset-0">
      <motion.div
        className="absolute inset-0 bg-[#0a0523]/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(20px) opacity(0)' }}
        animate={{ backdropFilter: 'blur(20px) opacity(1)' }}
      />

      <div className='flex flex-col gap-4 md:gap-[45px] px-2 md:px-0 pt-6 pb-4 md:py-10 max-w-[1280px] mx-auto h-full'>
        {isMobile ? (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-1 py-2.5"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[24px] font-blinko text-center text-white">
                History
              </p>
            </motion.div>

            <button className='absolute top-0 right-2' onClick={onClose}>
              <HexagonShape cornerRadius={6} strokeWidth={1}>
                <div className="flex justify-center items-center p-2">
                  <img src={icons.close} className='w-6 h-6' />
                </div>
              </HexagonShape>
            </button>
          </div>
        ) : (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-8"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[48px] font-blinko text-center text-white">
                History
              </p>
            </motion.div>

            <button className='absolute top-0 left-0' onClick={onClose}>
              <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                <div className="px-8 py-3">
                  <p className="text-base font-blinko text-white">
                    Back to Game
                  </p>
                </div>
              </InnerShape>
            </button>
          </div>
        )}

        <div className='flex-1 overflow-hidden'>
          <ContainerShape
            cornerDiagonal={isMobile ? 20 : 60}
            cornerRadius={isMobile ? 8 : 25}
            strokeWidth={2}
          >
            <div className='flex flex-col h-full py-6 px-3 md:px-12 md:py-12 overflow-auto'>
              {statsData && (
                <div className=''>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4'>
                    <InnerShape2 cornerDiagonal={16} cornerRadius={8} strokeWidth={1}>
                      <div className="flex flex-col gap-3 p-4">
                        <div className="flex items-center gap-2">
                          <InnerShape cornerDiagonal={10} cornerRadius={5} strokeWidth={1}>
                            <div className='p-2'>
                              <img src={icons.rocket} className="w-5 h-5" />
                            </div>
                          </InnerShape>
                          <p className="text-base font-blinko text-white/70 font-blinko">Total Games</p>
                        </div>
                        <p className="text-2xl font-black text-white">{statsData.totalGames}</p>
                      </div>
                    </InnerShape2>
                    <InnerShape2 cornerDiagonal={16} cornerRadius={8} strokeWidth={1}>
                      <div className="flex flex-col gap-3 p-4">
                        <div className="flex items-center gap-2">
                          <InnerShape cornerDiagonal={10} cornerRadius={5} strokeWidth={1}>
                            <div className='p-2'>
                              <img src={icons.currencyEthereum} className="w-5 h-5" />
                            </div>
                          </InnerShape>
                          <p className="text-base font-blinko text-white/70 font-blinko">Total Bet</p>
                        </div>
                        <p className="text-2xl font-black text-white">
                          {formatBalance(BigInt(statsData.totalBet || 0), TOKENS[ZERO_ADDRESS].decimals)}
                          <span className='text-sm font-bold text-white/70'> ETH</span>
                        </p>
                      </div>
                    </InnerShape2>
                    <InnerShape2 cornerDiagonal={16} cornerRadius={8} strokeWidth={1}>
                      <div className="flex flex-col gap-3 p-4">
                        <div className="flex items-center gap-2">
                          <InnerShape cornerDiagonal={10} cornerRadius={5} strokeWidth={1}>
                            <div className='p-2'>
                              <img src={icons.currencyEthereum} className="w-5 h-5" />
                            </div>
                          </InnerShape>
                          <p className="text-base font-blinko text-white/70 font-blinko">Total Win</p>
                        </div>
                        <p className="text-2xl font-black text-white">
                          {formatBalance(BigInt(statsData.totalPayout || 0), TOKENS[ZERO_ADDRESS].decimals)}
                          <span className='text-sm font-bold text-white/70'> ETH</span>
                        </p>
                      </div>
                    </InnerShape2>
                  </div>
                </div>
              )}

              <div className='mt-3'>
                {!isMobile && (
                  <div className='flex border-b border-solid border-white/20 pointer-events-auto'>
                    <div className="flex-1 flex items-center p-4">
                      <div className="flex items-center gap-2">
                        <p className="text-base font-semibold uppercase text-white/70">Date</p>
                      </div>
                    </div>
                    <div className="w-[20%] flex items-center p-4">
                      <div className="flex items-center gap-2">
                        <p className="text-base font-semibold uppercase text-white/70">Bet</p>
                      </div>
                    </div>
                    <div className="w-[20%] flex items-center p-4">
                      <div className="flex items-center gap-2">
                        <p className="text-base font-semibold uppercase text-white/70">Win</p>
                      </div>
                    </div>
                    <div className="w-[15%] flex items-center p-4">
                      <p className="text-base font-semibold uppercase text-white/70">Multiplier</p>
                    </div>
                    <div className="w-[15%] flex items-center p-4 justify-center">
                      <p className="text-base font-semibold uppercase text-white/70">Action</p>
                    </div>
                  </div>
                )}
              </div>

              {isHistoryLoading ? (
                <div className='flex justify-center items-center h-40'>
                  <Loader size="24px" stroke="#ffffff" />
                </div>
              ) : !historyData || historyData.length === 0 ? (
                <div className='flex justify-center items-center'>
                  <p className="text-white/70 text-center font-blinko">No history found</p>
                </div>
              ) : (
                <div className={`flex flex-col gap-1`}>
                  {historyData.map((game) => (
                    <HistoryItem
                      key={game.gameId}
                      game={game}
                      activeGameIds={activeGameIds}
                      isMobile={isMobile}
                      onAction={() => handleActiveGameAction(game)}
                      isSettling={!!settlingGames[game.gameId]}
                    />
                  ))}
                </div>
              )}

              {/* Pagination Component */}
              {!isHistoryLoading && totalPages > 1 && (
                <div className='flex justify-center items-center gap-4 pt-4'>
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className='disabled:opacity-50'
                  >
                    <InnerShape cornerDiagonal={10} cornerRadius={5} strokeWidth={1}>
                      <div className="px-5 py-2">
                        <p className="text-sm font-blinko text-white">Prev</p>
                      </div>
                    </InnerShape>
                  </button>

                  <div className='flex items-center gap-2'>
                    <p className="text-sm font-medium text-white">
                      Page {currentPage} of {totalPages}
                    </p>
                  </div>

                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className='disabled:opacity-50'
                  >
                    <InnerShape cornerDiagonal={10} cornerRadius={5} strokeWidth={1}>
                      <div className="px-5 py-2">
                        <p className="text-sm font-blinko text-white">Next</p>
                      </div>
                    </InnerShape>
                  </button>
                </div>
              )}
            </div>

          </ContainerShape>
        </div>
      </div>
    </div>
  )
};

const HistoryItem = ({ game, activeGameIds, isMobile, onAction, isSettling }: { game: BlinkoGame, activeGameIds: Set<string>, isMobile: boolean, onAction: () => void, isSettling: boolean }) => {
  const formattedDate = new Date(game.createdAt).toLocaleString()
  const bet = BigInt(game.betAmount)
  const win = BigInt(game.payoutAmount || '0')

  // Calculate multiplier: win / bet
  const multiplier = win > 0n ? (Number((win * 10000n) / bet) / 10000).toFixed(2) : '0'

  const isWin = win > 0n
  const isOnChainActive = activeGameIds.has(game.gameId.toLowerCase());
  
  // Definition based on "Graph First" check
  const isNotCreatedOnChain = !isOnChainActive && game.status === 'active';
  const isContinue = isOnChainActive && game.status === 'active';
  const isUnclaimed = isOnChainActive && game.status === 'won';

  // If determining button state:
  // Pending -> Yellow Continue
  // Unclaimed -> Green Claim (or Loader if settling)
  
  const renderActionButton = () => {
    if (isSettling) {
       return (
         <div className="bg-green-600/20 px-3 py-1.5 rounded border border-green-600/50 flex justify-center items-center h-[28px] w-[60px]">
           <Loader size="14px" stroke="#22c55e" />
         </div>
       )
    }

    if (isContinue) {
      return (
        <button
          onPointerDown={onAction}
          className="bg-yellow-600/20 hover:bg-yellow-600/40 text-yellow-500 text-xs font-bold px-3 py-1.5 rounded border border-yellow-600/50 transition-colors"
        >
          Continue
        </button>
      )
    }

    if (isUnclaimed) {
      return (
        <button
          onPointerDown={onAction}
          className="bg-green-600/20 hover:bg-green-600/40 text-green-500 text-xs font-bold px-3 py-1.5 rounded border border-green-600/50 transition-colors"
        >
          Claim
        </button>
      )
    }

    return (
       <a
        href={`/replay/${game.gameId}`}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-white/50 hover:text-white underline"
      >
        Replay
      </a>
    )
  }
  
  const renderMobileAction = () => {
    if (isSettling) {
        return <Loader size="14px" stroke="#22c55e" />;
    }
    if (isContinue) {
        return (
            <button
                onPointerDown={onAction}
                className="text-xs font-bold text-yellow-500 hover:text-yellow-400 underline"
            >
                Continue
            </button>
        )
    }
    if (isUnclaimed) {
        return (
            <button
                onPointerDown={onAction}
                className="text-xs font-bold text-green-500 hover:text-green-400 underline"
            >
                Claim
            </button>
        )
    }
    return (
        <a
            href={`/replay/${game.gameId}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-white/50 hover:text-white underline"
        >
            Replay
        </a>
    )
  }

  if (isNotCreatedOnChain) {
    return <></>
  }

  if (isMobile) {
    return (
      <motion.div className='flex flex-col rounded-lg bg-zinc-900/[0.25] p-3 text-white'>
        <div className="flex justify-between items-center mb-2">
          <div className="flex gap-2 items-center">
            <p className="text-[10px] text-white/50">{formattedDate}</p>
            {checkVersionMode(game).isHardMode && <span className="text-[8px] bg-red-500/20 text-red-300 px-1 rounded font-bold">HARD</span>}
          </div>
        </div>
        <div className="flex justify-between items-center text-sm">
          <div>
            <p className="text-xs text-white/50">Bet</p>
            <p className="font-medium">{formatBalance(bet, TOKENS[ZERO_ADDRESS].decimals)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-white/50">Win</p>
            <p className={`font-medium ${isWin ? 'text-green-400' : 'text-white'}`}>{formatBalance(win, TOKENS[ZERO_ADDRESS].decimals)}</p>
          </div>
        </div>
        <div className="flex justify-between items-center text-sm mt-2 pt-2 border-t border-white/10">
          <div>
            <p className="text-xs text-white/50">Multiplier</p>
            <p className="font-medium">{isContinue ? '-' : `${multiplier}x`}</p>
          </div>

          <div className="flex items-center gap-2">
            {renderMobileAction()}
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      className='flex items-center rounded-2xl h-[60px] text-white hover:bg-white/5 transition-colors'
    >
      <div className="flex-1 px-4 flex flex-col justify-center">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{formattedDate}</p>
          {checkVersionMode(game).isHardMode && (
            <span className="text-[10px] uppercase font-bold text-red-400 bg-red-900/30 px-1.5 rounded">Hard</span>
          )}
        </div>
      </div>
      <div className="w-[20%] px-4">
        <p className="text-sm font-semibold">{formatBalance(bet, TOKENS[ZERO_ADDRESS].decimals)} <span className='text-[10px] text-white/50'>ETH</span></p>
      </div>
      <div className="w-[20%] px-4">
        <div className="flex flex-col">
          <p className={`text-sm font-semibold ${isWin ? 'text-green-400' : ''}`}>{formatBalance(win, TOKENS[ZERO_ADDRESS].decimals)} <span className='text-[10px] text-white/50'>ETH</span></p>
        </div>
      </div>
      <div className="w-[15%] px-4">
        <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold ${isContinue ? 'bg-blue-500/20 text-blue-300' : isUnclaimed ? 'bg-yellow-500/20 text-yellow-300' : isWin ? 'bg-green-500/20 text-green-300' : 'bg-white/10 text-white/50'}`}>
          {isContinue ? 'Pending' : isUnclaimed ? 'Unclaimed' : `${multiplier}x`}
        </div>
      </div>
      <div className="w-[15%] px-4 flex justify-center">
        {renderActionButton()}
      </div>
    </motion.div>
  )
}
